import React, { useState, useEffect, useCallback } from 'react';
import { formatDisplayName } from '../utils/nameUtils';
import {
  Card,
  Button,
  TextInput,
  Select,
  Alert,
  Badge,
  Checkbox,
  Spinner,
  Modal,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Textarea
} from 'flowbite-react';
import {
  HiSearch,
  HiPlus,
  HiTrash,
  HiLockOpen,
  HiLockClosed,
  HiRefresh,
  HiShieldCheck,
  HiClock,
  HiCheck,
  HiX,
  HiEye
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { usersApi, type UserAccount } from '../api/users';
import { rbacApi, type Role } from '../api/rbac';
import { staffApi } from '../api/staff';
import { personsApi } from '../api/persons';
import { useAuth } from '../contexts/AuthContext';
import { SearchableSelect } from '../components/common/SearchableSelect';
import { useDebounce } from '../hooks/useDebounce';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roles: Role[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onSuccess, roles }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2) {
      searchPersons();
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch]);

  const searchPersons = async () => {
    try {
      setSearching(true);
      const results = await personsApi.search({ search: debouncedSearch, limit: 10 });
      // Filter out persons who already have user accounts
      const filtered = results.filter((p: any) => !p.hasUserAccount);
      setSearchResults(filtered);
    } catch (err) {
      console.error('Failed to search persons:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedPersonId || !selectedRoleId) {
      setError('Please select both a person and a role');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await usersApi.create({
        personId: selectedPersonId,
        roleId: selectedRoleId,
        isActive: true
      });

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user account');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedPersonId(null);
    setSelectedRoleId(null);
    setError(null);
    onClose();
  };

  return (
    <Modal show={isOpen} onClose={handleClose} dismissible popup size="lg">
      <div className="p-6">
        <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Create User Account</h3>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <div className="space-y-4">
          {/* Person Search */}
          <div>
            <Label htmlFor="person-search" className="mb-2">
              Search for Person (without existing account)
            </Label>
            <TextInput
              id="person-search"
              icon={HiSearch}
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searching && (
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching...</div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                {searchResults.map((person) => (
                  <div
                    key={person.personId}
                    className={`p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${
                      selectedPersonId === person.personId ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                    onClick={() => setSelectedPersonId(person.personId)}
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatDisplayName(person)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {person.email || 'No email'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div>
            <Label htmlFor="role" className="mb-2">
              Assign Role
            </Label>
            <Select
              id="role"
              value={selectedRoleId || ''}
              onChange={(e) => setSelectedRoleId(Number(e.target.value))}
            >
              <option value="">Select a role...</option>
              {roles.filter(r => r.isActive).map(role => (
                <option key={role.roleId} value={role.roleId}>
                  {role.roleName} - {role.roleDescription}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || !selectedPersonId || !selectedRoleId}>
            {loading ? <Spinner size="sm" /> : 'Create Account'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface RoleChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserAccount | null;
  roles: Role[];
}

const RoleChangeModal: React.FC<RoleChangeModalProps> = ({ isOpen, onClose, onSuccess, user, roles }) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setSelectedRoleId(user.roleId);
    }
  }, [user]);

  const handleChangeRole = async () => {
    if (!user || !selectedRoleId) return;

    try {
      setLoading(true);
      setError(null);

      await usersApi.changeRole(user.userId, {
        roleId: selectedRoleId,
        reason: reason || undefined
      });

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change role');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedRoleId(null);
    setReason('');
    setError(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Modal show={isOpen} onClose={handleClose} dismissible popup>
      <div className="p-6">
        <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Change User Role</h3>

        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Changing role for: <strong className="text-gray-900 dark:text-white">{formatDisplayName({ ...user.person, lastName: user.person.lastSurname })}</strong>
          <br />
          Current role: <strong className="text-gray-900 dark:text-white">{user.role.roleName}</strong>
        </p>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="new-role" className="mb-2">
              New Role
            </Label>
            <Select
              id="new-role"
              value={selectedRoleId || ''}
              onChange={(e) => setSelectedRoleId(Number(e.target.value))}
            >
              {roles.filter(r => r.isActive).map(role => (
                <option key={role.roleId} value={role.roleId}>
                  {role.roleName} - {role.roleDescription}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="reason" className="mb-2">
              Reason for Change (Optional)
            </Label>
            <Textarea
              id="reason"
              placeholder="Enter reason for role change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeRole}
            disabled={loading || selectedRoleId === user.roleId}
          >
            {loading ? <Spinner size="sm" /> : 'Change Role'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const UserManagementPage: React.FC = () => {
  // State management
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRoleId, setFilterRoleId] = useState<number | undefined>();
  const [filterRoleTag, setFilterRoleTag] = useState<string>('');
  const [roleTags, setRoleTags] = useState<Array<{ codeValue: string; shortDescription: string }>>([]);
  const [filterActive, setFilterActive] = useState<boolean | undefined>();
  const [filterHasLoggedIn, setFilterHasLoggedIn] = useState<boolean | undefined>();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAccount | null>(null);

  // Selection for bulk actions
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');

  // Pagination
  const [currentPage, _setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  const { currentUser, startImpersonation, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Load users and roles
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, rolesData] = await Promise.all([
        usersApi.list({
          search: debouncedSearchTerm || undefined,
          roleId: filterRoleId,
          roleTag: filterRoleTag || undefined,
          isActive: filterActive,
          hasLoggedIn: filterHasLoggedIn,
          page: currentPage,
          pageSize,
        }),
        rbacApi.listRoles({ includeInactive: false })
      ]);

      // Handle both array response and object response formats
      const usersList = Array.isArray(usersData) ? usersData : (usersData.users || []);
      setUsers(usersList);
      setRoles(Array.isArray(rolesData) ? rolesData : (rolesData.roles || []));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, filterRoleId, filterRoleTag, filterActive, filterHasLoggedIn, currentPage, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load staff role tags for filter dropdown
  useEffect(() => {
    const loadRoleTags = async () => {
      try {
        const tags = await staffApi.getRoleTags();
        setRoleTags(tags);
      } catch (err) {
        console.warn('Failed to load staff role tags:', err);
      }
    };
    loadRoleTags();
  }, []);

  // Handle selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(users.map(u => u.userId));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedUserIds(prev => [...prev, userId]);
    } else {
      setSelectedUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedUserIds.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const result = await usersApi.bulkAction({
        userIds: selectedUserIds,
        action: bulkAction as any,
      });

      setSuccessMessage(
        `Successfully processed ${result.successCount} users. ` +
        `${result.failedCount} failed.`
      );

      // Refresh data
      await loadData();

      // Clear selections
      setSelectedUserIds([]);
      setBulkAction('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  // Handle individual actions
  const handleActivateUser = async (userId: number) => {
    try {
      await usersApi.activate(userId);
      setSuccessMessage('User activated successfully');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate user');
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;

    try {
      await usersApi.deactivate(userId);
      setSuccessMessage('User deactivated successfully');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Are you sure you want to permanently delete this user account?')) return;

    try {
      await usersApi.delete(userId);
      setSuccessMessage('User deleted successfully');
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const openRoleChangeModal = (user: UserAccount) => {
    setSelectedUser(user);
    setShowRoleChangeModal(true);
  };

  const handleImpersonate = async (user: UserAccount) => {
    const displayName = formatDisplayName({ ...user.person, lastName: user.person.lastSurname });
    if (!confirm(`Are you sure you want to view the application as ${displayName} (${user.role.roleName})?`)) return;

    try {
      setError(null);
      await startImpersonation(user.userId);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start impersonation');
    }
  };

  // Format last login
  const formatLastLogin = (date?: string) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage user accounts, roles, and permissions</p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
            {successMessage}
          </Alert>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <TextInput
                  icon={HiSearch}
                  placeholder="Search by name, email, or auth ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <Select
                value={filterRoleId || ''}
                onChange={(e) => setFilterRoleId(
                  e.target.value === '' ? undefined : Number(e.target.value)
                )}
                className="min-w-[140px]"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName}
                  </option>
                ))}
              </Select>

              <SearchableSelect
                ariaLabel="Filter by staff role"
                options={roleTags.map(tag => ({ value: tag.codeValue, label: tag.shortDescription }))}
                value={filterRoleTag || null}
                onChange={(code) => setFilterRoleTag(code ?? '')}
                clearLabel="All Staff Roles"
                className="min-w-[200px]"
              />

              <Select
                value={filterActive === undefined ? '' : filterActive.toString()}
                onChange={(e) => setFilterActive(
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="min-w-[120px]"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>

              <Select
                value={filterHasLoggedIn === undefined ? '' : filterHasLoggedIn.toString()}
                onChange={(e) => setFilterHasLoggedIn(
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="min-w-[160px]"
              >
                <option value="">All Login Status</option>
                <option value="true">Has Logged In</option>
                <option value="false">Never Logged In</option>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                <HiPlus className="mr-2 h-4 w-4" />
                Create User
              </Button>

              {selectedUserIds.length > 0 && (
                <>
                  <Select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="w-48 text-sm"
                  >
                    <option value="">Bulk Actions...</option>
                    <option value="activate">Activate Selected</option>
                    <option value="deactivate">Deactivate Selected</option>
                    {currentUser?.role === 'superAdmin' && (
                      <option value="delete">Delete Selected</option>
                    )}
                  </Select>

                  <Button
                    size="sm"
                    color="blue"
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                  >
                    Apply ({selectedUserIds.length})
                  </Button>
                </>
              )}

              <Button size="sm" color="gray" onClick={loadData}>
                <HiRefresh className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="xl" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No users found. Try adjusting your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <TableHead>
                  <TableHeadCell>
                    <Checkbox
                      checked={selectedUserIds.length === users.length && users.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableHeadCell>
                  <TableHeadCell>User</TableHeadCell>
                  <TableHeadCell>Role</TableHeadCell>
                  <TableHeadCell>Organization Access</TableHeadCell>
                  <TableHeadCell>Last Login</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </TableHead>
                <TableBody className="divide-y">
                  {users.map((user) => (
                    <TableRow key={user.userId} className="bg-white dark:bg-gray-800">
                      <TableCell>
                        <Checkbox
                          checked={selectedUserIds.includes(user.userId)}
                          onChange={(e) => handleSelectUser(user.userId, e.target.checked)}
                        />
                      </TableCell>

                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {formatDisplayName({ ...user.person, lastName: user.person.lastSurname })}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.person.email || 'No email'}
                          </div>
                          {user.authProviderId && (
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Auth: {user.authProviderId.substring(0, 8)}...
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center">
                          <HiShieldCheck className="mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{user.role.roleName}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {user.permissions?.length ?? 0} permissions
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        {(user.organizationAccess?.length ?? 0) > 0 ? (
                          <div>
                            <div className="text-sm text-gray-900 dark:text-white">
                              {user.organizationAccess![0].organizationName}
                            </div>
                            {(user.organizationAccess?.length ?? 0) > 1 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                +{(user.organizationAccess?.length ?? 0) - 1} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">Global Access</span>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <HiClock className="mr-1 h-3 w-3 text-gray-400 dark:text-gray-500" />
                          {formatLastLogin(user.lastLoginAt)}
                        </div>
                      </TableCell>

                      <TableCell>
                        {user.isActive ? (
                          <Badge color="success">
                            <HiCheck className="mr-1 h-3 w-3" />
                            Active
                          </Badge>
                        ) : (
                          <Badge color="failure">
                            <HiX className="mr-1 h-3 w-3" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            size="xs"
                            color="gray"
                            onClick={() => openRoleChangeModal(user)}
                            title="Change Role"
                            aria-label="Change Role"
                          >
                            <HiShieldCheck className="h-3 w-3" />
                          </Button>

                          {currentUser?.role === 'superAdmin' && user.userId !== currentUser?.userId && user.isActive && !isImpersonating && (
                            <Button
                              size="xs"
                              color="purple"
                              onClick={() => handleImpersonate(user)}
                              title="View as this user"
                              aria-label="Impersonate"
                            >
                              <HiEye className="h-3 w-3" />
                            </Button>
                          )}

                          {user.isActive ? (
                            <Button
                              size="xs"
                              color="yellow"
                              onClick={() => handleDeactivateUser(user.userId)}
                              title="Deactivate"
                              aria-label="Deactivate"
                            >
                              <HiLockClosed className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              color="green"
                              onClick={() => handleActivateUser(user.userId)}
                              title="Activate"
                              aria-label="Activate"
                            >
                              <HiLockOpen className="h-3 w-3" />
                            </Button>
                          )}

                          {currentUser?.role === 'superAdmin' && (
                            <Button
                              size="xs"
                              color="red"
                              onClick={() => handleDeleteUser(user.userId)}
                              title="Delete"
                              aria-label="Delete"
                            >
                              <HiTrash className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Modals */}
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setSuccessMessage('User account created successfully');
            loadData();
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          roles={roles}
        />

        <RoleChangeModal
          isOpen={showRoleChangeModal}
          onClose={() => {
            setShowRoleChangeModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setSuccessMessage('User role changed successfully');
            loadData();
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          user={selectedUser}
          roles={roles}
        />
      </div>
    </div>
  );
};